import { Fragment, useContext } from "react";
import classnames from "classnames";
import { useTranslation } from "next-i18next";

// Styles
import Styles from "./SrcDest.module.scss";

// Components
import { LorrySrcDest, LoadsSrcDest, LoadingState } from "@vahak/core/dist/custom-types/src-dest";
import { MARKET_TYPE } from "@vahak/core/dist/constants/market";
import { ComponentState } from "@vahak/core/dist/constants/misc";

import Tooltip from "../tool-tip/Tooltip";
import RouteState from "../../../user-profile/my-routes/route-state/RouteState";
import EllipsisTooltip from "@vahak/core/dist/components/ellipsis-tooltip/EllipsisTooltip";

// Icons
import DoubleArrow from "@vahak/core/dist/icons/doubleArrow.svg";

// Context
import Skeleton from "@vahak/core/dist/components/skeleton/Skeleton";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";

type SrcDestProps = LorrySrcDest | LoadsSrcDest;

type Props = SrcDestProps | LoadingState;

const SrcDest = ({ state = ComponentState.NORMAL, ...prop }: Props) => {
    const { cardCategory, source, id = "", ...props } = prop as SrcDestProps;
    const { t } = useTranslation(["common"]);
    const { routes, position = "bottom" } = props as LorrySrcDest;
    const { destination } = props as LoadsSrcDest;

    const isMobile = useMediaQuery({ queryType: "mobile" });
    if (state === ComponentState.LOADING) {
        return (
            <div className={Styles.lorrySrcDestBox}>
                <div className={classnames(Styles.sourceDestWrap, ComponentState.LOADING && Styles.loading)}>
                    <p className={Styles.source}>
                        <Skeleton width={150} height={18} />
                    </p>
                    <p className={Styles.dest}>
                        <Skeleton width={150} height={18} />
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            {(cardCategory === MARKET_TYPE.Lorry || cardCategory === MARKET_TYPE.LORRIES) && (
                <div className={Styles.lorrySrcDestBox}>
                    <div className={classnames(Styles.sourceDestWrap)}>
                        <div className={Styles.source} id={`${id}--source`}>
                            {source}
                        </div>
                        <div className={Styles.dest} id={`${id}--dest`}>
                            {t("common:towards")} <DoubleArrow />{" "}
                            {!isMobile
                                ? routes?.slice?.(0, 1).map((route) => (
                                      <Fragment key={route.id}>
                                          {route.name} {routes?.length > 1 && "|"}{" "}
                                      </Fragment>
                                  ))
                                : (routes?.length || 0) < 2 &&
                                  routes?.slice?.(0, 1).map((route) => (
                                      <Fragment key={route.id}>
                                          <EllipsisTooltip
                                              textValue={route.name || ""}
                                              greaterThanValue={8}
                                              styles={{
                                                  maxWidth: "60px",
                                                  position: "absolute",
                                                  top: "0",
                                                  paddingLeft: "2px"
                                              }}
                                          />
                                      </Fragment>
                                  ))}{" "}
                            {(routes?.length || 0) > 1 && (
                                <>
                                    <Tooltip
                                        tooltipToggler={
                                            <span className={Styles.viewStates}>{`${isMobile ? "" : "+"}${
                                                !isMobile ? (routes?.length || 0) - 1 : routes?.length || 0
                                            } ${t("common:states")}`}</span>
                                        }
                                        tooltipContent={
                                            routes &&
                                            (!isMobile ? (
                                                <span className={Styles.routes}>
                                                    {routes?.slice?.(1, routes?.length)?.map((route, index) => (
                                                        <span className={Styles.route} key={index}>
                                                            {route.name}
                                                        </span>
                                                    ))}
                                                </span>
                                            ) : (
                                                <div className={Styles.routeWrapperMobile}>
                                                    {routes.map((route, index) => (
                                                        <RouteState
                                                            key={index}
                                                            routeValue={route.name}
                                                            isSectionRoute={true}
                                                            routeClass={Styles.myRoutes}
                                                            onClickSelect={() => {}}
                                                        />
                                                    ))}
                                                </div>
                                            ))
                                        }
                                        appearOnHover={!isMobile}
                                        position={position}
                                        fallbackText={<>No routes</>}
                                        shouldOpenInBottomSheet={isMobile}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {(cardCategory === MARKET_TYPE.LOAD || cardCategory === MARKET_TYPE.LOADS) && (
                <>
                    <div className={Styles.sourceDestWrap}>
                        <div className={Styles.source} id={`${id}--source`}>
                            {source !== "undefined" ? source : ""}
                        </div>
                        <div className={Styles.dest} id={`${id}--dest`}>
                            {destination !== "undefined" ? destination : ""}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default SrcDest;
